/**
 * API Service for Notification Languages.
 * Languages are MERCHANT-SCOPED (per-merchant configuration).
 *
 * Backend: Alaric.Services.Messaging - LanguagesController
 * Base route: /api/v1/languages
 */

import { Result } from "../../../models/Result";
import { LanguageDto, CreateLanguageRequest, UpdateLanguageRequest } from "../../../models/notifications";
import { messagingHttpService } from "./MessagingHttpService";

const BASE_PATH = "/api/v1/languages";

class LanguagesApiService {
    /**
     * Get all languages for a merchant.
     * @param merchantId The merchant ID.
     * @param includeInactive If true, includes inactive languages.
     */
    getAll = async (merchantId: number, includeInactive: boolean = false): Promise<Result<LanguageDto[]>> => {
        const params = new URLSearchParams();
        params.append("merchantId", merchantId.toString());
        if (includeInactive) {
            params.append("includeInactive", "true");
        }
        return messagingHttpService.get<LanguageDto[]>(`${BASE_PATH}?${params.toString()}`);
    };

    /**
     * Get only active languages for a merchant.
     * @param merchantId The merchant ID.
     */
    getActive = async (merchantId: number): Promise<Result<LanguageDto[]>> => {
        return this.getAll(merchantId, false);
    };

    /**
     * Get a specific language by ID.
     * @param languageId The language GUID.
     */
    getById = async (languageId: string): Promise<Result<LanguageDto>> => {
        return messagingHttpService.get<LanguageDto>(`${BASE_PATH}/${languageId}`);
    };

    /**
     * Get a specific language by ISO code for a merchant.
     * @param merchantId The merchant ID.
     * @param isoCode The ISO language code (e.g., "en", "bg").
     */
    getByIsoCode = async (merchantId: number, isoCode: string): Promise<Result<LanguageDto>> => {
        return messagingHttpService.get<LanguageDto>(`${BASE_PATH}/by-code/${encodeURIComponent(isoCode)}?merchantId=${merchantId}`);
    };

    /**
     * Create a new language.
     * @param request The language to create.
     */
    create = async (request: CreateLanguageRequest): Promise<Result<{ languageId: string }>> => {
        return messagingHttpService.post<{ languageId: string }>(BASE_PATH, request);
    };

    /**
     * Update an existing language.
     * @param languageId The language GUID to update.
     * @param request The updated language data.
     */
    update = async (languageId: string, request: UpdateLanguageRequest): Promise<Result<void>> => {
        return messagingHttpService.put<void>(`${BASE_PATH}/${languageId}`, request);
    };

    /**
     * Activate a language.
     * @param languageId The language GUID to activate.
     */
    activate = async (languageId: string): Promise<Result<void>> => {
        return this.update(languageId, { isActive: true });
    };

    /**
     * Deactivate a language.
     * @param languageId The language GUID to deactivate.
     */
    deactivate = async (languageId: string): Promise<Result<void>> => {
        return this.update(languageId, { isActive: false });
    };

    /**
     * Delete a language.
     * Note: Cannot delete if template localizations are using this language.
     * @param languageId The language GUID to delete.
     */
    delete = async (languageId: string): Promise<Result<void>> => {
        return messagingHttpService.delete<void>(`${BASE_PATH}/${languageId}`);
    };

    /**
     * Check if a language with the given ISO code exists for a merchant.
     * @param merchantId The merchant ID.
     * @param isoCode The ISO language code to check.
     */
    existsByIsoCode = async (merchantId: number, isoCode: string): Promise<boolean> => {
        const result = await this.getByIsoCode(merchantId, isoCode);
        return result.success;
    };
}

const languagesApiService = new LanguagesApiService();

export { languagesApiService };
